import React from "react";
import { Empty } from "antd";
import { formatDateTime, formatDurationSeconds } from "../services/firestoreTracking.service";

function eventTone(status) {
  if (status === "live") {
    return "border-emerald-400/30 bg-emerald-500/10 text-emerald-100";
  }
  if (status === "ended_without_recovery") {
    return "border-slate-400/30 bg-slate-500/10 text-slate-100";
  }
  if (status === "offline") {
    return "border-rose-400/30 bg-rose-500/10 text-rose-100";
  }
  return "border-amber-400/30 bg-amber-500/10 text-amber-100";
}

function buildTrackingEventEntry(event) {
  if (event?.type === "tracking_session_started") {
    return {
      id: `tracking-${event.id}`,
      label: "Session started",
      details: event?.sessionId ? `Session ${event.sessionId}` : "Driver telemetry connected.",
      when: event?.initialRecordedAtMs || event?.clientTimestampMs || event?.createdAtMs,
      status: "live",
      durationSeconds: null,
    };
  }

  if (event?.type === "tracking_session_ended") {
    return {
      id: `tracking-${event.id}`,
      label: "Session ended",
      details: event?.reason || "Driver telemetry stopped.",
      when: event?.finalRecordedAtMs || event?.createdAtMs,
      status: "offline",
      durationSeconds: null,
    };
  }

  if (event?.type === "tracking_gap_resolved") {
    return {
      id: `tracking-${event.id}`,
      label: "Gap resolved",
      details: event?.classification
        ? event.classification.replace(/_/g, " ")
        : "Buffered telemetry resumed.",
      when: event?.gapResolvedAtMs || event?.createdAtMs,
      status: "recovering",
      durationSeconds: event?.gapDurationSeconds || null,
    };
  }

  return null;
}

function buildComplianceEventEntry(event) {
  if (event?.type !== "tracking_lost") return null;

  if (event?.status === "resolved") {
    return {
      id: `compliance-${event.id}-resolved`,
      label: "Tracking resumed",
      details:
        event?.outageDurationSeconds != null
          ? `Recovered after ${formatDurationSeconds(event.outageDurationSeconds)}.`
          : "Heartbeat recovered.",
      when: event?.resolvedAtMs || event?.gapResolvedAtMs || event?.eventAtMs,
      status: "recovering",
      durationSeconds: event?.outageDurationSeconds || null,
    };
  }

  if (event?.status === "recovering") {
    return {
      id: `compliance-${event.id}-recovering`,
      label: "Recovery being monitored",
      details: "A reconnect signal was received, but stable heartbeats are still being verified.",
      when: event?.provisionalResolvedAtMs || event?.eventAtMs,
      status: "recovering",
      durationSeconds: event?.gapDurationSeconds || null,
    };
  }

  if (event?.status === "ended_without_recovery") {
    return {
      id: `compliance-${event.id}-ended`,
      label: "Trip ended without recovery",
      details:
        event?.outageDurationSeconds != null
          ? `Tracking stayed down for ${formatDurationSeconds(event.outageDurationSeconds)} before trip end.`
          : "Tracking did not recover before trip end.",
      when: event?.closedAtMs || event?.eventAtMs,
      status: "ended_without_recovery",
      durationSeconds: event?.outageDurationSeconds || null,
    };
  }

  return {
    id: `compliance-${event.id}-lost`,
    label: "Tracking lost",
    details: "Heartbeat overdue. Driver phone is offline or the tracking runtime stopped.",
    when: event?.eventAtMs,
    status: "offline",
    durationSeconds: event?.outageDurationSeconds || null,
  };
}

export const TrackingActivityPanel = ({ trackingEvents = [], complianceEvents = [] }) => {
  const feed = [
    ...trackingEvents.map(buildTrackingEventEntry),
    ...complianceEvents.map(buildComplianceEventEntry),
  ]
    .filter(Boolean)
    .sort((left, right) => (right?.when || 0) - (left?.when || 0))
    .slice(0, 8);

  return <div className="rounded-[32px] border border-[#191c25] bg-[linear-gradient(180deg,#10151d_0%,#080b12_100%)] px-6 py-6 text-white shadow-[0_24px_70px_rgba(2,6,23,0.28)]">
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8fd0ad]">Connection Terminal</div>
        <div className="mt-2 text-[1.9rem] font-semibold leading-tight text-white">Online / Offline Feed</div>
        <div className="mt-2 max-w-[520px] text-sm leading-7 text-[#9db0c3]">
          Live session starts, heartbeat loss, recovery monitoring, and reconnect events from Firestore subcollections.
        </div>
      </div>
      <div className="text-right">
        <div className="text-[1.8rem] font-semibold text-white">{feed.length}</div>
        <div className="text-xs uppercase tracking-[0.22em] text-[#90a1b4]">Recent events</div>
      </div>
    </div>

    <div className="mt-5 rounded-[24px] border border-white/10 bg-black/25 px-4 py-4">
      <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)_auto] gap-4 px-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#7f92a5]">
        <div>Log</div>
        <div>Details</div>
        <div>When</div>
      </div>
      <div className="mt-4 flex flex-col gap-3">
        {feed.length === 0 ? <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-5">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span className="text-[#90a1b4]">No connection events logged for this trip yet.</span>} />
        </div> : feed.map((event) => <div
          key={event.id}
          className={`grid grid-cols-1 gap-3 rounded-2xl border px-4 py-4 md:grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)_auto] ${eventTone(event.status)}`}
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold uppercase tracking-[0.14em]">{event.label}</div>
            {event.durationSeconds ? <div className="mt-1 text-xs text-white/70">{formatDurationSeconds(event.durationSeconds)}</div> : null}
          </div>
          <div className="min-w-0 text-sm text-white/85">{event.details}</div>
          <div className="text-sm font-medium text-white/80">{formatDateTime(event.when)}</div>
        </div>)}
      </div>
    </div>
  </div>;
};

export const TrackingActivityHighlights = ({ trackingEvents = [], complianceEvents = [] }) => {
  const highlights = [
    ...trackingEvents.map(buildTrackingEventEntry),
    ...complianceEvents.map(buildComplianceEventEntry),
  ]
    .filter((event) => event && ["Session started", "Tracking lost"].includes(event.label))
    .sort((left, right) => (right?.when || 0) - (left?.when || 0))
    .slice(0, 4);

  if (highlights.length === 0) return null;

  return <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
    {highlights.map((event) => <div
      key={event.id}
      className={`rounded-2xl border px-4 py-4 shadow-[0_10px_28px_rgba(15,23,42,0.04)] ${event.status === "offline" ? "border-[#f5cbd2] bg-[#fff1f3]" : "border-[#cfe8d8] bg-[#f4fff7]"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${event.status === "offline" ? "text-[#c43f5b]" : "text-[#3f8a59]"}`}>
            {event.label}
          </div>
          <div className="mt-2 text-sm leading-6 text-[#44362d]">{event.details}</div>
        </div>
        <div className="text-sm font-semibold text-[#1f1712]">{formatDateTime(event.when)}</div>
      </div>
    </div>)}
  </div>;
};
